import React, { createContext, useContext, useState, useEffect } from 'react';

const StoreContext = createContext();

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }
  return context;
};

export const StoreProvider = ({ children }) => {
  // Account state
  const [mtAccount, setMtAccount] = useState(null);
  
  // Account balance for risk calculation
  const [accountBalance, setAccountBalance] = useState(10000); // Default $10,000
  
  // License state
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [licenseKey, setLicenseKeyState] = useState(null);
  
  // Robot state
  const [isRunning, setIsRunning] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  
  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Theme colors
  const colors = isDarkMode ? {
    background: '#0f172a',
    surface: '#1e293b',
    surfaceMuted: '#334155',
    text: '#f1f5f9',
    textSecondary: '#94a3b8',
    textMuted: '#64748b',
    border: '#475569',
    primary: '#3b82f6',
    primaryMuted: '#1e40af',
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
  } : {
    background: '#f8fafc',
    surface: '#ffffff',
    surfaceMuted: '#f1f5f9',
    text: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#94a3b8',
    border: '#e2e8f0',
    primary: '#3b82f6',
    primaryMuted: '#dbeafe',
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
  };
  
  // Toggle theme
  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('isDarkMode', String(newValue));
      return newValue;
    });
  };
  
  // Trade settings
  const [tradeSettings, setTradeSettings] = useState({
    lotSize: 0.01,
    action: "BOTH",
    tradeEveryMinutes: 5,
    triggerPoints: 10,
    riskPercent: 1,
    maxOpenTrades: 3,
    useSLTP: true,
    profitTargetsUSD: {
      EURUSD: "8",
      GBPUSD: "9",
      USDJPY: "8",
      XAUUSD: "7",
      BTCUSD: "10",
    },
    stopLossUSD: {
      EURUSD: "12",
      GBPUSD: "14",
      USDJPY: "12",
      XAUUSD: "10",
      BTCUSD: "12",
    },
  });

  // Load saved state from localStorage on mount
  useEffect(() => {
    const savedAccount = localStorage.getItem('mtAccount');
    const savedLicense = localStorage.getItem('licenseKey');
    const savedSettings = localStorage.getItem('tradeSettings');
    const savedBot = localStorage.getItem('selectedBot');
    const savedTheme = localStorage.getItem('isDarkMode');
    
    if (savedAccount) {
      try {
        setMtAccount(JSON.parse(savedAccount));
      } catch (e) {
        console.error('Error parsing saved account:', e);
      }
    }
    
    if (savedLicense) {
      setLicenseKeyState(savedLicense);
      setIsLicenseValid(true);
    }
    
    if (savedSettings) {
      try {
        setTradeSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error('Error parsing saved settings:', e);
      }
    }
    
    if (savedBot) {
      try {
        setSelectedBot(JSON.parse(savedBot));
      } catch (e) {
        console.error('Error parsing saved bot:', e);
      }
    }
    
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'true');
    }
  }, []);

  // Save account to localStorage
  const handleSetMtAccount = (account) => {
    setMtAccount(account);
    if (account) {
      localStorage.setItem('mtAccount', JSON.stringify(account));
    } else {
      localStorage.removeItem('mtAccount');
    }
  };

  // Set license key
  const setLicenseKey = (key, isValid = false) => {
    setLicenseKeyState(key);
    setIsLicenseValid(isValid);
    if (key) {
      localStorage.setItem('licenseKey', key);
    } else {
      localStorage.removeItem('licenseKey');
    }
  };

  // Start/stop robot
  const startRobot = () => setIsRunning(true);
  const stopRobot = () => setIsRunning(false);

  // Save trade settings
  const handleSetTradeSettings = (settings) => {
    setTradeSettings(settings);
    localStorage.setItem('tradeSettings', JSON.stringify(settings));
  };

  // Save selected bot
  const handleSetSelectedBot = (bot) => {
    setSelectedBot(bot);
    if (bot) {
      localStorage.setItem('selectedBot', JSON.stringify(bot));
    } else {
      localStorage.removeItem('selectedBot');
    }
  };

  const value = {
    // Account
    mtAccount,
    setMtAccount: handleSetMtAccount,
    accountBalance,
    setAccountBalance,
    
    // License
    isLicenseValid,
    licenseKey,
    setLicenseKey,
    
    // Robot
    isRunning,
    startRobot,
    stopRobot,
    selectedBot,
    setSelectedBot: handleSetSelectedBot,
    
    // Trade settings
    tradeSettings,
    setTradeSettings: handleSetTradeSettings,
    
    // Theme
    isDarkMode,
    toggleTheme,
    colors,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};

export default StoreContext;
